/**
 * A node's hover card on the editor's canvas: what the node is; In, each
 * input field of its capability and where its value comes from; and Out,
 * what later steps and gates read of it. Every line comes from the draft
 * and the capabilities' schemas, as the inspector's do, so the card asks
 * the agent for nothing.
 *
 * A gate node's card lists every condition in order; a condition node's, its
 * own conditions and steps (Feature 79, revision 1, D27).
 */
import type { LinkageDecision, LinkageSource, LinkageStep } from '@optum/api-client/types';

import { branchSteps, testLines } from './condition-words.js';
import { ELSE, findCondition, type Condition } from './conditions.js';
import { FIRST, type Draft } from './draft.js';
import { conditionWords, itemConditionWords } from './gate-lines.js';
import { isListFilter } from './lists.js';
import { mappableFields, readablePath, shortRef, type Schemas } from './references.js';
import { isCall, isSchedule } from './start.js';
import { showValue } from './ValueInput.js';

/** One line of a card; a faint one says what is not there. */
export interface CardLine {
  text: string;
  faint: boolean;
}

export interface NodeCard {
  /** `product_suggestion · product_suggestion_v1 · independent`. */
  title: string;
  /** Null on `first`, which nothing maps into. */
  inputs: CardLine[] | null;
  outputs: CardLine[];
  /** On a decision, its branches as they are tried (Feature 79 D21). */
  branches?: CardLine[] | undefined;
}

/** A step's mode in words, as its chip on the canvas says it. */
function modeWords(step: LinkageStep): string {
  if (step.mode === 'independent') return 'independent';
  if (step.mode === 'write_back') return `write-back → ${step.write_back?.target ?? '—'}`;
  return 'merged';
}

/** Where a list filter's pick reads within an item: `bant_id`, or the item itself. */
function pickWords(pick: string): string {
  return readablePath(pick.split('.').slice(1)).join('.') || 'the item';
}

/** Where one input field's value comes from, or that nothing gives it one. */
function inputLine(field: string, source: LinkageSource | undefined, required: boolean): CardLine {
  if (source === undefined) {
    return required
      ? { text: `${field} · not mapped, and it is required`, faint: false }
      : { text: `${field} · not mapped, its default applies`, faint: true };
  }
  if ('value' in source) return { text: `${field} = ${showValue(source.value)}`, faint: false };
  if (isListFilter(source)) {
    const where = source.where ? ` where ${itemConditionWords(source.where)}` : '';
    const pick = source.pick ? `, pick ${pickWords(source.pick)}` : '';
    return { text: `${field} ← ${shortRef(source.ref)}${where}${pick}`, faint: false };
  }
  return { text: `${field} ← ${shortRef(source.ref)}`, faint: false };
}

/** Each input field of a step's capability, then any mapped field its schema does not name. */
function inputLines(step: LinkageStep, schemas: Schemas): CardLine[] {
  const fields = mappableFields(schemas.get(step.capability_id));
  const named = new Set(fields.map((f) => f.name));
  const lines = [
    ...fields.map((f) => inputLine(f.name, step.mapping[f.name], f.required)),
    ...Object.entries(step.mapping)
      .filter(([field]) => !named.has(field))
      .map(([field, source]) => inputLine(field, source, false)),
  ];
  return lines.length > 0 ? lines : [{ text: 'no input fields known here', faint: true }];
}

/** Whether a stored reference reads the node: its input or its result. */
function readsNode(ref: string, node: string): boolean {
  if (node === FIRST && (ref === 'input' || ref.startsWith('input.'))) return true;
  return ref.startsWith(`steps.${node}.`);
}

/** What a reference reads within its node, without the node's name: `note`, `input.conversation_id`. */
function within(ref: string): string {
  const short = shortRef(ref);
  const mark = short.indexOf(' › ');
  return mark < 0 ? short : short.slice(mark + 3);
}

/** What later steps and gates read of the node, once each. */
function outputLines(draft: Draft, node: string): CardLine[] {
  const lines = new Set<string>();
  for (const step of draft.steps) {
    for (const source of Object.values(step.mapping)) {
      if ('ref' in source && readsNode(source.ref, node)) {
        lines.add(`${step.name} reads ${within(source.ref)}`);
      }
    }
  }
  for (const link of draft.links) {
    for (const rule of link.rules.items) {
      if (readsNode(rule.ref, node)) {
        lines.add(`the gate ${link.from} → ${link.to} reads ${within(rule.ref)}`);
      }
    }
  }
  return lines.size > 0
    ? [...lines].map((text) => ({ text, faint: false }))
    : [{ text: 'nothing after it reads it', faint: true }];
}

/** The steps a decision's branch links to, or that it has none yet. */
function branchTargets(draft: Draft, decision: string, branch: string): string {
  const to = branchSteps(draft, decision, branch);
  return to.length > 0 ? to.join(', ') : 'no step yet';
}

/** A condition node's lines: what it tests, or what the else catches, then its steps. */
function conditionNodeLines(
  draft: Draft,
  decision: LinkageDecision,
  condition: Condition,
): CardLine[] {
  const switching = decision.mode === 'switch';
  const tests =
    condition.number === null
      ? [switching ? 'when no case matches' : 'when nothing above holds']
      : testLines(decision, condition.branch);
  return [
    ...(switching && condition.number !== null
      ? [
          {
            text: `switch on ${decision.ref ? shortRef(decision.ref) : 'no field yet'}`,
            faint: false,
          },
        ]
      : []),
    ...tests.map((text) => ({
      text,
      faint: text === 'no condition yet' || text === 'no value yet',
    })),
    { text: `→ ${branchTargets(draft, decision.name, condition.branch)}`, faint: false },
  ];
}

/** A decision's lines: a switch's field, then each branch and its conditions, then the else. */
function decisionLines(draft: Draft, decision: LinkageDecision): CardLine[] {
  const lines: CardLine[] = [];
  const line = (text: string, faint = false) => lines.push({ text, faint });
  const switching = decision.mode === 'switch';
  if (switching) line(`switch on ${decision.ref ? shortRef(decision.ref) : 'no field yet'}`);
  decision.branches.forEach((branch, i) => {
    const to = branchTargets(draft, decision.name, branch.name);
    if (switching) {
      line(`${(branch.values ?? []).join(', ') || 'no value yet'} → ${to}`);
      return;
    }
    line(`${i + 1} ${branch.name} → ${to}`);
    const join = branch.rules?.join === 'any' ? 'or' : 'and';
    conditionWords(branch.rules ?? { join: 'all', items: [] }).forEach((words, n) =>
      line(`  ${n === 0 ? 'when' : join} ${words}`),
    );
  });
  if (decision.else)
    line(`${switching ? 'default' : ELSE} → ${branchTargets(draft, decision.name, ELSE)}`);
  else line('none · when nothing holds, nothing runs', true);
  return lines;
}

/** The card of a node of the draft, or null when no node has that id. */
export function nodeCard(draft: Draft, id: string, schemas: Schemas): NodeCard | null {
  if (id === FIRST) {
    const capability = draft.firstCapabilityId || 'no capability yet';
    // A call has no result when its steps start: they read its input (Feature 78 D3).
    const title = isSchedule(draft.start)
      ? `first · ${capability} · run at each fire with its fixed input`
      : isCall(draft.start)
        ? `first · ${capability} · the call: its input, no result yet`
        : `first · ${capability} · the completed task`;
    return { title, inputs: null, outputs: outputLines(draft, FIRST) };
  }
  const found = findCondition(draft.decisions, id);
  if (found) {
    const { decision, condition } = found;
    const title =
      condition.number === null
        ? `${condition.kind} · in ${decision.name}`
        : `${condition.branch} · ${condition.number} · ${condition.kind} · in ${decision.name}`;
    return {
      title,
      inputs: null,
      outputs: [],
      branches: conditionNodeLines(draft, decision, condition),
    };
  }
  const decision = draft.decisions.find((d) => d.name === id);
  if (decision) {
    const kind = decision.mode === 'switch' ? 'switch gate' : 'if-else gate';
    return {
      title: `${decision.name} · ${kind} · after ${decision.from || 'nothing yet'} · tried in order`,
      inputs: null,
      outputs: [],
      branches: decisionLines(draft, decision),
    };
  }
  const step = draft.steps.find((s) => s.name === id);
  if (!step) return null;
  return {
    title: `${step.name} · ${step.capability_id} · ${modeWords(step)}`,
    inputs: inputLines(step, schemas),
    outputs: outputLines(draft, step.name),
  };
}
